import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

const TOKEN_KEY = 'vesper_admin_token';
const EXPIRES_KEY = 'vesper_admin_token_expires';

export function useAdminSession() {
  const { t } = useTranslation();
  const [token, setToken] = useState<string | null>(() =>
    sessionStorage.getItem(TOKEN_KEY)
  );
  const [needsPasswordSetup, setNeedsPasswordSetup] = useState(false);

  const [isValid, setIsValid] = useState<boolean>(() => {
    const storedToken = sessionStorage.getItem(TOKEN_KEY);
    const expires = sessionStorage.getItem(EXPIRES_KEY);
    return !!(storedToken && expires && new Date(expires) > new Date());
  });

  const verify = async (password: string) => {
    try {
      // 先檢查 session 是否有效
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      let accessToken = session?.access_token;
      if (sessionError || !accessToken) {
        toast.error(t('admin.verify.sessionExpired'));
        // 嘗試刷新 token
        const { data: { session: refreshedSession }, error: refreshError } = await supabase.auth.refreshSession();
        if (refreshError || !refreshedSession?.access_token) {
          toast.error(t('admin.verify.pleaseRelogin'));
          return false;
        }
        accessToken = refreshedSession.access_token;
      }

      const { data, error } = await supabase.functions.invoke('admin-verify', {
        body: { password },
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      });

      if (error) {
        // Parse error message if possible
        let errorMsg = error.message;
        let errorCode = '';
        
        console.group('Admin Verify Error Debug');
        console.error('Full Error Object:', error);
        console.error('Error Message:', error.message);
        console.error('Error Details:', error.details);
        console.error('Error Hint:', error.hint);
        console.groupEnd();

        try {
            // Try to parse the error message if it's a JSON string
            const body = JSON.parse(error.message);
            if (body.error) errorMsg = body.error;
            if (body.code) errorCode = body.code;
            console.log('Parsed Error Body:', body);
        } catch (e) {
            console.log('Error message is not JSON:', error.message);
        }

        // Check if password not set
        if (errorCode === 'PASSWORD_NOT_SET') {
          setNeedsPasswordSetup(true);
          toast.error(t('admin.verify.passwordNotSet'));
          return false;
        }

        // Check if session invalid
        if (errorCode === 'SESSION_INVALID' || errorCode === 'NO_AUTH_HEADER') {
          toast.error(t('admin.verify.sessionExpired'));
          return false;
        }

        toast.error(errorMsg || t('admin.verify.failed'));
        return false;
      }

      if (data?.error) {
          // Handle error returned in 200 OK response
          const errorMsg = data.error;
          const errorCode = data.code;
          
          if (errorCode === 'PASSWORD_NOT_SET') {
              setNeedsPasswordSetup(true);
              toast.error(t('admin.verify.passwordNotSet'));
              return false;
          }

          if (errorCode === 'SESSION_INVALID' || errorCode === 'NO_AUTH_HEADER') {
              toast.error(t('admin.verify.sessionExpired'));
              return false;
          }

          toast.error(errorMsg || t('admin.verify.failed'));
          return false;
      }

      sessionStorage.setItem(TOKEN_KEY, data.token);
      sessionStorage.setItem(EXPIRES_KEY, data.expires_at);
      setToken(data.token);
      setIsValid(true);
      setNeedsPasswordSetup(false);
      toast.success(t('admin.verify.success'));
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : t('admin.verify.error');
      toast.error(message);
      return false;
    }
  };

  const logout = () => {
    setToken(null);
    setIsValid(false);
    sessionStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(EXPIRES_KEY);
    toast.info("已登出管理員 Session");
  };

  // Check expiration periodically or on mount
  useEffect(() => {
    const checkValidity = () => {
      const expires = sessionStorage.getItem(EXPIRES_KEY);
      const output = !!(token && expires && new Date(expires) > new Date());
      if (isValid !== output) {
          setIsValid(output);
          if (!output && token) {
              // Token expired
              logout();
          }
      }
    };
    
    checkValidity();
    const interval = setInterval(checkValidity, 60000); // Check every minute
    return () => clearInterval(interval);
  }, [token, isValid]);

  return { token, isValid, needsPasswordSetup, verify, logout };
}
